import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useDashboard } from '../../../contexts/DashboardContext';
import FormFieldRenderer from '../../FormFieldRenderer';
import ApprovalCard from '../../studio/flowBuilder/ApprovalCard';
import { isAdvisorFieldVisible } from '../../../data/sfAdvisorIntake';

function submitResultMessage(output, { citizen = false } = {}) {
  if (output?.awaitingApproval) return null;
  if (citizen) {
    return output?.routing
      ? 'Application path updated — see Your next path, then continue to Applications.'
      : 'Submitted, but no path was returned. Check the address and try again.';
  }
  return output ? 'Submitted and flow completed.' : 'Submitted, but the flow did not return output. Check app diagnostics or flow logs.';
}

export default function DashboardForm({ panelId, outputKey = 'form_prefill', config }) {
  const {
    getSlice, submitStaticFormPanel, loadingPanelId, runtimeAuditEvents, consumeHandoffPrefill,
    pendingApproval, decideAppApproval, structuredOutput, validateParcelAddress, config: dashConfig,
    formResetKey = 0,
  } = useDashboard();
  const approval = pendingApproval?.panelId === panelId ? pendingApproval : null;
  const raw = getSlice(panelId, outputKey);
  const isCitizen = Boolean(
    dashConfig?.citizenMode
    || dashConfig?.metadata?.interviewFlow?.citizenMode
    || config?.citizenSubmitLabel,
  );
  const submitLabel = config?.citizenSubmitLabel || (isCitizen ? 'Get my application path' : 'Submit to flow');
  const submittingLabel = isCitizen ? 'Finding path…' : 'Running flow…';

  const staticFields = Array.isArray(config?.formFields) ? config.formFields : [];
  const hasStatic = staticFields.length > 0;
  const shouldValidateAddress = Boolean(
    config?.validateAddress
    ?? staticFields.some((f) => ['address', 'location'].includes(String(f.name || f.id || ''))),
  );

  const [localValues, setLocalValues] = useState({});
  const [submitMessage, setSubmitMessage] = useState(null);
  const [addressValidating, setAddressValidating] = useState(false);

  const fieldSig = useMemo(
    () => staticFields.map((f) => `${f.id || f.name || ''}:${f.type || ''}`).join('|'),
    [staticFields],
  );

  useEffect(() => {
    const fromFields = {};
    staticFields.forEach((f) => {
      const key = f?.name || f?.id;
      if (!key) return;
      const v = f.defaultValue ?? f.value;
      if (v == null || v === '') return;
      fromFields[key] = v;
    });
    const initial = (config?.initialValues && typeof config.initialValues === 'object')
      ? { ...config.initialValues }
      : {};
    setLocalValues({ ...initial, ...fromFields });
    setSubmitMessage(null);
  }, [fieldSig, formResetKey]);

  useEffect(() => {
    const prefill = consumeHandoffPrefill?.();
    if (prefill && typeof prefill === 'object') {
      setLocalValues((prev) => ({ ...prefill, ...prev }));
    }
  }, [consumeHandoffPrefill, fieldSig]);

  // Map click / parcel lookup / session hydrate writes into structuredOutput.form_prefill — mirror into the form.
  useEffect(() => {
    const prefill = structuredOutput?.form_prefill;
    if (!prefill || typeof prefill !== 'object') return;
    const fieldKeys = new Set(
      staticFields.map((f) => f?.name || f?.id).filter(Boolean),
    );
    const always = [
      'address', 'location', 'propertyType', 'projectType', 'description', 'proposedUse',
      'approxSqft', 'servesAlcohol', 'outdoorSeating', 'foodHandling', 'structuralChange',
      'lat', 'lng', 'sessionId', 'packageId', 'applicationTypeId', 'applicantName',
      'contactEmail', 'contactPhone', 'projectAddress', 'projectDescription',
    ];
    always.forEach((k) => fieldKeys.add(k));
    setLocalValues((prev) => {
      const next = { ...prev };
      let changed = false;
      fieldKeys.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(prefill, key)) return;
        if (prefill[key] == null || prefill[key] === '') return;
        if (String(prev[key] || '') === String(prefill[key])) return;
        // Don't overwrite user typing for long text unless empty
        if (
          (key === 'description' || key === 'projectDescription' || key === 'message')
          && String(prev[key] || '').trim()
        ) {
          return;
        }
        next[key] = prefill[key];
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [structuredOutput?.form_prefill, fieldSig]);

  const handleFieldChange = (key, value) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
  };

  const showWhenRules = config?.showWhenRules
    || structuredOutput?.showWhenRules
    || {};

  const visibleFields = useMemo(
    () => staticFields.filter((field) => {
      if (!field?.showWhen) return true;
      return isAdvisorFieldVisible(field, localValues, showWhenRules);
    }),
    [staticFields, localValues, showWhenRules],
  );

  // Drop answers for fields that no longer apply (e.g. remodel → hide alcohol).
  useEffect(() => {
    if (!staticFields.some((f) => f?.showWhen)) return undefined;
    setLocalValues((prev) => {
      let changed = false;
      const next = { ...prev };
      staticFields.forEach((field) => {
        if (!field?.showWhen) return;
        const key = field.name || field.id;
        if (!key || next[key] == null || next[key] === '' || next[key] === false) return;
        if (isAdvisorFieldVisible(field, next, showWhenRules)) return;
        delete next[key];
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [
    staticFields,
    showWhenRules,
    localValues.propertyType,
    localValues.projectType,
    localValues.description,
    localValues.proposedUse,
  ]);

  // Debounced SF parcel validation when address (or map-synced address) is set.
  useEffect(() => {
    if (!shouldValidateAddress || !validateParcelAddress) return undefined;
    const address = String(localValues.address || localValues.location || '').trim();
    if (address.length < 8) return undefined;
    const last = structuredOutput?.parcel;
    const matched = String(last?.location || last?.geocode?.matchedAddress || '').trim().toLowerCase();
    if (last?.success && matched && matched === address.toLowerCase()) return undefined;
    const t = setTimeout(async () => {
      setAddressValidating(true);
      try {
        await validateParcelAddress({
          address,
          lon: localValues.lng ?? localValues.lon,
          lat: localValues.lat,
          propertyType: localValues.propertyType || localValues.projectType,
          proposedUse: localValues.proposedUse,
          description: localValues.description,
        });
      } finally {
        setAddressValidating(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [
    shouldValidateAddress,
    validateParcelAddress,
    structuredOutput?.parcel,
    localValues.address,
    localValues.location,
    localValues.lat,
    localValues.lng,
    localValues.lon,
    localValues.propertyType,
    localValues.projectType,
    localValues.proposedUse,
    localValues.description,
  ]);

  const isSubmitting = loadingPanelId === panelId;
  const intakeGates = dashConfig?.metadata?.interviewFlow?.intakeGates || {};
  const minAddressLength = Number(intakeGates.minAddressLength) > 0
    ? Number(intakeGates.minAddressLength)
    : 8;
  const addressValue = String(localValues.address || localValues.location || '').trim();
  const propertyTypeValue = String(localValues.propertyType || localValues.projectType || '').trim();
  const gateIssues = [];
  if (isCitizen && intakeGates.requireAddress !== false && addressValue.length < minAddressLength) {
    gateIssues.push(`Address (at least ${minAddressLength} characters)`);
  }
  if (isCitizen && intakeGates.requirePropertyType !== false && !propertyTypeValue) {
    gateIssues.push('Property / project type');
  }
  const missingRequired = visibleFields.filter((field, idx) => {
    if (!field.required) return false;
    const key = field.name || field.id || `idx_${idx}`;
    const value = localValues[key];
    return value == null || String(value).trim() === '';
  });
  const latestAudit = (Array.isArray(runtimeAuditEvents) ? runtimeAuditEvents : [])
    .filter((event) => event.panelId === panelId)
    .slice(-3)
    .reverse();
  const blockedByGates = gateIssues.length > 0;

  const handleSubmit = async () => {
    if (!submitStaticFormPanel) return;
    setSubmitMessage(null);
    if (blockedByGates) {
      setSubmitMessage(`Complete before advising: ${gateIssues.join(', ')}.`);
      return;
    }
    const visibleKeys = new Set(
      visibleFields.map((f, idx) => f.name || f.id || `idx_${idx}`),
    );
    const payload = Object.fromEntries(
      Object.entries(localValues).filter(([key]) => visibleKeys.has(key)
        || ['lat', 'lng', 'lon', 'location'].includes(key)),
    );
    const output = await submitStaticFormPanel(panelId, payload);
    setSubmitMessage(submitResultMessage(output, { citizen: isCitizen }));
  };

  const handleDecide = async (decision) => {
    setSubmitMessage(null);
    const output = await decideAppApproval(decision);
    if (output) setSubmitMessage(submitResultMessage(output));
  };

  const entries = useMemo(() => {
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(raw).map(([k, v]) => ({
      key: k,
      value: typeof v === 'object' ? JSON.stringify(v) : String(v ?? ''),
    }));
  }, [raw]);

  const prefillBlock = entries.length > 0 ? (
    <Box sx={{ mt: hasStatic ? 2 : 0 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Form prefill (from flow output)
      </Typography>
      <Stack spacing={1.5}>
        {entries.map(({ key, value }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={key} size="small" variant="outlined" />
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  ) : null;

  if (hasStatic) {
    return (
      <Box sx={{
        p: 2,
        height: isCitizen ? 'auto' : '100%',
        minHeight: 120,
        overflow: isCitizen ? 'visible' : 'auto',
      }}
      >
        {(config.formTitle || config.formDescription) && (
          <Box sx={{ mb: 2 }}>
            {config.formTitle && (
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {config.formTitle}
              </Typography>
            )}
            {config.formDescription && (
              <Typography variant="body2" color="text.secondary">
                {config.formDescription}
              </Typography>
            )}
          </Box>
        )}
        {!config.formDescription && !isCitizen && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Form fields configured in Studio. Submit runs the panel&apos;s bound V2 flow using the field input mapping.
          </Typography>
        )}
        {submitMessage && <Alert severity="info" sx={{ mb: 1.5 }}>{submitMessage}</Alert>}
        <Stack spacing={2}>
          {visibleFields.map((field, idx) => {
            const key = field.name || field.id || `idx_${idx}`;
            return (
              <FormFieldRenderer
                key={key}
                type={field.type || 'text'}
                label={field.label || field.name || key}
                placeholder={field.placeholder}
                value={localValues[key] ?? (field.type === 'checkbox' ? false : '')}
                onChange={(e) => handleFieldChange(key, e?.target?.value ?? e)}
                required={Boolean(field.required)}
                options={field.options || []}
                min={field.min}
                max={field.max}
                step={field.step}
                rows={field.rows || 3}
                maxRows={field.maxRows || 8}
              />
            );
          })}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || missingRequired.length > 0 || blockedByGates}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            data-testid={isCitizen ? 'citizen-advise-submit' : 'studio-form-submit'}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
          {approval && (
            <ApprovalCard
              approval={approval.approval}
              busy={approval.busy}
              error={approval.error}
              onDecide={handleDecide}
            />
          )}
          {addressValidating && (
            <Typography variant="caption" color="text.secondary">
              Validating address against SF Planning GIS…
            </Typography>
          )}
          {blockedByGates && (
            <Alert severity="warning" sx={{ py: 0.5 }} data-testid="citizen-intake-gate">
              Need address and property type before we can advise.
              {' '}
              Missing:
              {' '}
              {gateIssues.join(', ')}
              .
            </Alert>
          )}
          {missingRequired.length > 0 && (
            <Typography variant="caption" color="warning.main">
              Complete required fields:
              {' '}
              {missingRequired.map((field) => field.label || field.name || field.id).join(', ')}
            </Typography>
          )}
          {latestAudit.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Recent submission audit
              </Typography>
              <Stack spacing={0.5}>
                {latestAudit.map((event) => (
                  <Typography key={event.id} variant="caption" color="text.secondary">
                    {event.at}
                    {' '}
                    -
                    {' '}
                    {event.type}
                    {event.durationMs != null ? ` (${event.durationMs}ms)` : ''}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
        {prefillBlock && (
          <>
            <Divider sx={{ my: 2 }} />
            {prefillBlock}
          </>
        )}
      </Box>
    );
  }

  if (!entries.length) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 120 }}>
        <Typography color="text.secondary">No static form fields. Add fields on the form panel in V2 Studio, or use chat to run a flow for prefill.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {prefillBlock}
    </Box>
  );
}
